// Shared by the records, pipeline and send pages: types, URL state, the one way to write, and small words.
import { useCallback, useEffect, useState } from 'react'
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { errorText, useKit } from '../kit'

export type Track = 'partner' | 'startup' | 'f26'
export type StageDef = { key: string; label: string }
export type Stages = Record<Track, StageDef[]>
export type Meta = { stages: Stages; owners: string[]; me: string | null; owner: boolean }
export type Sync = Record<string, string>

export type Rel = {
  id: string; company_id: string; track: Track; stage: string; stage_label: string; owner: string | null
  hold_until: string | null; hold_reason: string | null; blocked: string | null
  person?: string | null; person_id?: string | null; tier?: string | null; route?: string | null; priority?: string | null; in_range?: boolean | null
  stage_changed_at?: string; stage_changed_by?: string | null; stage_from?: string | null; warm_path?: string | null; warm_strength?: string | null; ask?: string | null
}
export type Card = { id: string; status: string; submitted_at: string | null; created_at: string }
export type PageRow = { id: string; kind: Track; slug: string; url: string; greeting?: string | null; sender?: string | null; state: string; checked_at: string | null; check_status: number | null }
export type Task = { id: string; title: string; owner: string | null; due_on: string | null; done_at: string | null; done_by: string | null; created_at: string }
export type Connection = { founder: string; status: string | null; note: string | null; connected_on: string | null; claimed: boolean }
export type TimelineRow = {
  body?: string | null; body_note?: string | null; said?: string | null; meta?: { from?: string; to?: string[]; cc?: string[]; body_source?: string; subject?: string }
  kind: string; id: string; at: string | null; title: string; detail?: string | null; actor?: string | null; channel?: string; direction?: string
  src?: string | null; source?: string; person?: string | null; person_id?: string | null; page_url?: string | null; read_at?: string | null
  attio_url?: string | null; owner?: string | null; due_on?: string | null; done?: boolean
}
export type Timeline = { at: string; rows: TimelineRow[]; kinds: Record<string, number>; truncated: boolean; sync: Sync; covers: string }

export const TRACK_LABEL: Record<Track, string> = { partner: 'Partner', startup: 'Startup', f26: 'F26' }
export const BLOCKED_STAGES = new Set(['hold', 'off_limits', 'off', 'not_us', 'lost', 'not_now'])
export const CARD_WORD: Record<string, string> = { draft: 'Draft', received: 'Request sent', invited: 'Invited', withdrawn: 'Withdrawn' }
export const STATUS_WORD: Record<string, string> = { messaged: 'Messaged', replied: 'Replied', meeting: 'Meeting', pass: 'Not a fit' }
/** An imported status can be anything the old lists held, like "dm drafted". It shows as written. */
export const statusWord = (s: string | null | undefined): string => (s ? STATUS_WORD[s] ?? s : '')
export const statusOptions = (current?: string | null) => [
  { value: '', label: 'Not set' },
  ...Object.entries(STATUS_WORD).map(([value, label]) => ({ value, label })),
  ...(current && !STATUS_WORD[current] ? [{ value: current, label: current }] : []),
]
export const SYNC_LIMIT = 15 * 60      // the app sync runs every 5 minutes
export const SNAPSHOT_LIMIT = 26 * 3600
export const REASON_MIN = 3

export const ROUTE_WORD: Record<string, string> = { cold_eligible: 'Cold', warm_connection: 'Warm connection', warm_yc: 'Warm YC', f26: 'F26', none: 'None' }
export const routeWord = (r: string | null | undefined): string => (r ? ROUTE_WORD[r] ?? r : '')

export const CHANNEL_WORD: Record<string, string> = { email: 'Email', linkedin: 'LinkedIn', bookface: 'Bookface', chat: 'Chat', call: 'Call', meeting: 'Meeting', intro: 'Intro', other: 'Other' }
/** A tier in words. The imported "reject:..." tiers say why a company was left out. */
export const tierWord = (t: string | null | undefined): string | null => {
  if (!t) return null
  if (t.startsWith('reject:')) return `Left out: ${t.slice(7).replaceAll('_', ' ')}`
  return /^[A-Z0-9]$/.test(t) ? `tier ${t}` : t.replaceAll('_', ' ')
}
export type Imported = { at: string | null; emails_through: string | null; covers: string }

const CHANNEL_SRC: Record<string, string> = { linkedin: 'dm', bookface: 'bookface', chat: 'chat' }
/** The &src= tag for a row. By default it follows the channel, so an email never goes out tagged as a DM. */
export const sourceTag = (choice: string, channel: string): string | null =>
  (choice === 'none' ? null : choice === 'auto' ? CHANNEL_SRC[channel] ?? null : choice)

/** A hold or do-not-contact in words. A row whose stage alone blocks it already shows that stage. */
export const holdWord = (r: { blocked: string | null; stage_label: string }): string | null =>
  r.blocked && r.blocked !== `Stage is ${r.stage_label}` ? r.blocked : null

/** A founder's key as a name. Anyone else's address stays as it is. */
export const nameOf = (s: string | null | undefined): string => (!s ? '' : s.includes('@') ? s : s.charAt(0).toUpperCase() + s.slice(1))

/** Only http(s) links render. Anything else in a record stays text. */
export const safeHref = (url: string | null | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined

export function withParam(url: string, key: string, value: string): string {
  try {
    const u = new URL(url)
    u.searchParams.set(key, value)
    return u.toString()
  } catch {
    return url
  }
}

export const linkedinHref = (url: string | null | undefined, who: string | null | undefined, company: string | null | undefined): string =>
  safeHref(url) ?? `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent([who, company].filter(Boolean).join(' '))}`

/** Fills a send's message. {page} takes the link, {first} and {company} take the record's words. */
export function fillMessage(text: string, o: { link?: string | null; person?: string | null; company?: string | null; strip?: boolean }): string {
  const body = o.strip ? text.split('\n').filter((l) => !l.includes('{page}')).join('\n') : text
  return body.replaceAll('{page}', o.link ?? '').replaceAll('{first}', (o.person ?? '').split(' ')[0]).replaceAll('{company}', o.company ?? '')
}

export const useMeta = () => useQuery({ queryKey: ['crm', 'meta'], queryFn: () => api<Meta>('GET', '/api/staff/crm/meta'), staleTime: 300_000 })

/** Filters live in the URL, so a filtered view is a link. */
export function useFilters() {
  const [sp, set] = useSearchParams()
  const get = (k: string) => sp.get(k) ?? ''
  const put = useCallback((patch: Record<string, string | null | undefined>) => set((cur) => {
    const n = new URLSearchParams(cur)
    for (const [k, v] of Object.entries(patch)) { if (v) n.set(k, v); else n.delete(k) }
    return n
  }, { replace: true }), [set])
  return { sp, get, put }
}

export const queryOf = (sp: URLSearchParams, keys: string[], extra: Record<string, string> = {}): string => {
  const n = new URLSearchParams(extra)
  for (const k of keys) { const v = sp.get(k); if (v) n.set(k, v) }
  const s = n.toString()
  return s ? `?${s}` : ''
}

/** The list a record was opened from, so Previous and Next walk it. Per tab, and fine to lose. */
const LIST_KEY = 'st-record-list'
export function rememberList(kind: string, ids: string[]) {
  const back = window.location.pathname + window.location.search     // where Esc goes
  try { sessionStorage.setItem(LIST_KEY, JSON.stringify({ kind, ids, back })) } catch { /* Previous and Next just stay hidden */ }
}
export function listBack(): string | null {
  try { return (JSON.parse(sessionStorage.getItem(LIST_KEY) ?? 'null') as { back?: string } | null)?.back ?? null } catch { return null }
}
export function neighbours(kind: string, id: string): { prev: string | null; next: string | null } {
  try {
    const got = JSON.parse(sessionStorage.getItem(LIST_KEY) ?? 'null') as { kind: string; ids: string[] } | null
    const i = got && got.kind === kind ? got.ids.indexOf(id) : -1
    if (i >= 0) return { prev: got!.ids[i - 1] ?? null, next: got!.ids[i + 1] ?? null }
  } catch { /* same */ }
  return { prev: null, next: null }
}

export function useDebounced<T>(value: T, ms = 200): T {
  const [v, set] = useState(value)
  useEffect(() => { const t = setTimeout(() => set(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

/** One list, a page at a time. `next` fetches the rest, so nothing past the first page is out of reach. Only a list still on
 * its first page polls, so a poll is one short page and a long list never reloads under the reader. */
export function usePaged<P extends { rows: R[]; next: string | null }, R>(key: unknown[], path: string, poll?: number) {
  const q = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api<P>('GET', pageParam ? `${path}${path.includes('?') ? '&' : '?'}after=${encodeURIComponent(pageParam)}` : path),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    placeholderData: keepPreviousData,
    refetchInterval: (s) => (poll && (s.state.data?.pages.length ?? 0) <= 1 ? poll : false),
  })
  const pages = q.data?.pages
  return { head: pages?.[0], rows: pages?.flatMap((p) => p.rows) ?? [], error: q.error, more: !!q.hasNextPage, loading: q.isFetchingNextPage, load: () => void q.fetchNextPage() }
}

/** Every write goes through this. Refreshes what the write touched, says what went wrong, and returns null on failure. */
export function useWrite() {
  const qc = useQueryClient()
  const { toast } = useKit()
  return useCallback(async <T = unknown>(method: 'POST' | 'PATCH', path: string, body: unknown, ok?: string): Promise<T | null> => {
    try {
      const r = await api<T>(method, path, body)
      await qc.invalidateQueries({ queryKey: ['crm'] })
      void qc.invalidateQueries({ queryKey: ['staff', 'counts'] })
      void qc.invalidateQueries({ queryKey: ['staff', 'today'], predicate: (q) => q.queryKey[2] !== 'feed' })
      if (ok) toast(ok)
      return r
    } catch (e) {
      toast(errorText(e), { bad: true })
      return null
    }
  }, [qc, toast])
}
